import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { executeRecaptcha } from '../utils/recaptcha';

const FRONTEND_DEBUG = process.env.REACT_APP_FRONTEND_DEBUG === 'TRUE';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(FRONTEND_DEBUG ? 'debug' : null);
  const [captchaValidated, setCaptchaValidated] = useState(FRONTEND_DEBUG);
  const [isVerifyingCaptcha, setIsVerifyingCaptcha] = useState(false);
  const { login } = useAuth();

  const resetCaptcha = () => {
    if (FRONTEND_DEBUG) return;
    setCaptchaValidated(false);
    setCaptchaToken(null);
  };

  const handleCaptchaClick = async () => {
    if (FRONTEND_DEBUG || isVerifyingCaptcha || isLoading) return;

    if (captchaValidated) {
      resetCaptcha();
      return;
    }

    setError('');
    setIsVerifyingCaptcha(true);
    try {
      const token = await executeRecaptcha('login');
      setCaptchaToken(token);
      setCaptchaValidated(true);
    } catch {
      resetCaptcha();
      setError('Vérification anti-robot échouée, réessayez');
    } finally {
      setIsVerifyingCaptcha(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!captchaValidated || !captchaToken) {
      setError('Veuillez valider le captcha avant de vous connecter');
      return;
    }

    setIsLoading(true);

    try {
      await login({ email, password, role: 'admin', captcha: captchaToken });
    } catch (err) {
      resetCaptcha();
      const message = err instanceof Error ? err.message : 'Login failed';
      if (
        message.includes('reCAPTCHA') ||
        message.includes('captcha') ||
        message.includes('Captcha')
      ) {
        setError('Vérification anti-robot échouée, réessayez');
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const canSubmit = !isLoading && !isVerifyingCaptcha && captchaValidated && !!captchaToken;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to access the admin panel
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleCaptchaClick}
            disabled={isVerifyingCaptcha || isLoading || FRONTEND_DEBUG}
            className={`w-full flex items-center gap-3 px-3.5 py-3 border border-gray-300 rounded-md bg-slate-50 text-left transition-opacity ${
              isVerifyingCaptcha ? 'opacity-70 cursor-wait' : 'hover:bg-slate-100 cursor-pointer'
            } disabled:cursor-not-allowed`}
            aria-pressed={captchaValidated}
          >
            <span
              className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                captchaValidated
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-white border-gray-400'
              }`}
              aria-hidden="true"
            >
              {isVerifyingCaptcha ? (
                <svg
                  className="animate-spin h-3.5 w-3.5 text-indigo-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : captchaValidated ? (
                <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : null}
            </span>
            <span className="flex-1 text-sm text-gray-800">
              {isVerifyingCaptcha
                ? 'Vérification…'
                : captchaValidated
                  ? 'Captcha validé'
                  : "Je ne suis pas un robot — cliquez pour vérifier"}
            </span>
            <span className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
              reCAPTCHA
            </span>
          </button>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={!canSubmit}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
            <p className="mt-3 text-center text-xs text-gray-500">
              This site is protected by reCAPTCHA and the Google{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-700"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-700"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
